import DriveSidebar from 'components/layout/DriveSidebar'
import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useEffect, useState } from 'react'
import Page from 'rui/widgets/Page'
import { Helmet } from 'react-helmet'
import { Container, Form, Spacer, Text } from 'roadie-ui'
import Panel, { PanelTitle, PanelHeading } from 'components/layout/Panel'
import { AccountSetting, DrivingProfile } from 'types/profile'
import { setLanguagePreference } from 'actions/languagePreference'
import {
  fullySupportedLanguages,
  LocalizedRichText,
  partiallySupportedLanguages,
  supportedLanguages,
  useLocalization,
} from 'localization'
import { getLatestAccountSetting } from 'util/profile'

const LanguagePreferencePage: React.FC = () => {
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const seattleDriver = useSelector<any, boolean>((state) => state.seattleDriver)
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const accountSettings = useSelector<any, AccountSetting[]>((state) => state.accountSettings || [])
  const { t } = useLocalization()

  const availableLanguages = seattleDriver ? supportedLanguages : fullySupportedLanguages
  const storedLanguage = getLatestAccountSetting(accountSettings, 'preferred_language')
  const initialLanguage =
    storedLanguage && availableLanguages.some((language) => language.value === storedLanguage)
      ? storedLanguage
      : 'english'

  const [language, setLanguage] = useState(initialLanguage)
  const dispatch = useDispatch()
  const handleLanguageSelection = useCallback(
    (_: string | undefined, selectedLanguage: string) => {
      setLanguagePreference(profile.id, selectedLanguage)(dispatch)
      setLanguage(selectedLanguage)
    },
    [dispatch, profile.id]
  )

  useEffect(() => {
    setLanguage(initialLanguage)
  }, [initialLanguage])

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_common_language_preference')}</title>
      </Helmet>
      {!isWebView && <PanelTitle>{t('dweb_common_language_preference')}</PanelTitle>}
      <Panel left>
        <PanelHeading>{t('dweb_common_language_preference')}</PanelHeading>
        <Form.Radio.Group onChange={handleLanguageSelection} value={language}>
          <Container.Base $padding="1rem 0 0 0" $color="neutral-black">
            {fullySupportedLanguages.map(({ value, labelKey }) => (
              <React.Fragment key={value}>
                <Form.Radio.Field value={value}>
                  <Text.Body1 $textAlign="left" $textTransform="capitalize">
                    {t(labelKey)}
                  </Text.Body1>
                </Form.Radio.Field>
                <Spacer $height="1.6rem" />
              </React.Fragment>
            ))}
            {seattleDriver && (
              <>
                <Text.Subtitle1 $fontSize="1.5rem">
                  {t('dweb_language_preference_partial_support_disclaimer')}
                </Text.Subtitle1>
                <Spacer $height="1.6rem" />
                {partiallySupportedLanguages.map(({ value, labelKey }) => (
                  <React.Fragment key={value}>
                    <Form.Radio.Field value={value}>
                      <Text.Body1 $textAlign="left" $textTransform="capitalize">
                        {t(labelKey)}
                      </Text.Body1>
                    </Form.Radio.Field>
                    <Spacer $height="1.6rem" />
                  </React.Fragment>
                ))}
              </>
            )}
          </Container.Base>
        </Form.Radio.Group>
        {seattleDriver && (
          <Text.Subtitle1 $fontSize="1.5rem">
            <LocalizedRichText
              translationKey="dweb_language_preference_missing_language"
              params={{
                supportEmailLink: <a href="mailto:legal@roadie.com">legal@roadie.com</a>,
              }}
            />
          </Text.Subtitle1>
        )}
      </Panel>
    </Page>
  )
}

export default LanguagePreferencePage
